import { useState, useMemo } from "react";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from "../../../components/ui/dialog";
import { Formik } from "formik";
import { Button } from "../../../components/ui/button";
import { FormikInput, FormikSearchSelect } from "../../../components/formik-components";
import * as yup from "yup";
import Common from "../../../helper/common";
import {
  ResultStatus,
} from "../../../constants/appConstant";
import { AppCheckBox } from "../../../components/appComponents";
import { TreeSelect } from 'antd';
import AntDesignTree, { defaultTagRender } from "../../../components/antdesignTree/antdesignTree";

// Validation schema
const validationSchema = yup.object({
  name: yup.string().required("Name is required"),
  endpoint: yup.string().required("Endpoint is required"),
  // controller: yup.string().required("Controller is required"),
  // action: yup.string().required("Action is required"),
  // icon: yup.string().required("Icon is required"),
  // menutabid: yup.number().nullable(),
  // premiumallowed: yup.boolean().required("Select premium access"),
  // isdisplayon: yup.boolean().required("Select display status"),
  // isclubspecific: yup.boolean().required("Select club specificity"),
  //isdefaultview: yup.boolean().required("Select default view"),
  // parentId: yup.number().nullable(),
  // parentname: yup.string().nullable(),
});

const buildTree = (items) => {
  const map = {};
  const roots = {};
  const OTHERS_KEY = 'others';
  const OTHERS_TITLE = 'Others (No Menutab)';

  // 1. Create map of all nodes
  items.forEach((item) => {
    map[item.id] = {
      ...item,
      key: item.id,
      value: item.id, // Add value property for TreeSelect compatibility
      title: item.name,
      children: []
    };
    // Create menutab root if missing
    if (item.menutab) {
      if (!roots[item.menutab]) {
        roots[item.menutab] = {
          key: `menutab-${item.menutabid}`,
          value: `menutab-${item.menutabid}`,
          title: item.menutab,
          isMenutab: true,
          menutabid: item.menutabid,
          children: []
        };
      }
    } else {
      // For items with no menutab, create 'Others' root if not exists
      if (!roots[OTHERS_KEY]) {
        roots[OTHERS_KEY] = {
          key: OTHERS_KEY,
          value: OTHERS_KEY,
          title: OTHERS_TITLE,
          isMenutab: true,
          menutabid: null,
          children: []
        };
      }
    }
  });

  // 2. Link parent-child relationships
  items.forEach((item) => {
    if (item.parentId && map[item.parentId]) {
      map[item.parentId].children.push(map[item.id]);
    } else if (item.menutab) {
      roots[item.menutab].children.push(map[item.id]);
    } else {
      roots[OTHERS_KEY].children.push(map[item.id]);
    }
  });

  // 3. Return menutab roots as array
  return Object.values(roots);
};
const toBoolean = (value) => {
  if (typeof value === "string") {
    const v = value.trim().toLowerCase();
    return v === "1" || v === "true" || v === "yes" || v === "y";
  }
  return value === 1 || value === true;
};

const AddEditRoutes = ({
  id,
  open,
  setOpen,
  onClose,
  selectedItem,
  menuTabs,
  defaultPermissionList,
}) => {
  const [parentDialogOpen, setParentDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const initialValues = {
    id: selectedItem?.id ?? null,
    name: selectedItem?.name ?? "",
    endpoint: selectedItem?.endpoint ?? "",
    //controller: selectedItem?.controller ?? "",
    //action: selectedItem?.action ?? "",
    icon: selectedItem?.icon ?? "",
    menutabid: selectedItem?.menutabid ?? null,
    menutab: selectedItem?.menutab ?? "",
    // Normalize various representations (1/0, true/false, yes/no) to boolean for UI
    ispremium: toBoolean(selectedItem?.ispremium),
    isdisplayon: toBoolean(selectedItem?.isdisplayon),
    isclubspecific: toBoolean(selectedItem?.isclubspecific),
    isnewfeature: toBoolean(selectedItem?.isnewfeature),
    //isdefaultview: selectedItem?.isdefaultview ?? false,
    parentId: selectedItem?.parentId ?? null,
    parentname: selectedItem?.parentname ?? "",
    sortOrder: selectedItem?.sortOrder ?? null,
  };

  const handleSubmit = async (values, actions) => {
    // Prevent multiple submissions
    if (isSubmitting) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      let assignToAllClubs = false;
      
      // Show confirmation dialog only if isclubspecific is checked
      if (values.isclubspecific) {
        try {
          const userChoice = await Common.AntDesignModal.showConfirm({
            title: "Assign to All Clubs",
            text: "Do you want to assign this route to all clubs and their admins?",
            confirmButtonText: "Yes",
            cancelButtonText: "No"
          });
          
          // Only proceed if user explicitly chose Yes or No
          if (userChoice === true) {
            assignToAllClubs = true;
          } else if (userChoice === false) {
            assignToAllClubs = false;
          } else {
            // User closed the dialog or cancelled - stop submission
            actions.setSubmitting(false);
            setIsSubmitting(false);
            return;
          }
        } catch (error) {
          // If modal throws an error (e.g., when closed), stop submission
          actions.setSubmitting(false);
          setIsSubmitting(false);
          return;
        }
      }
      
      // If parentId is not a number, make it null
      const parentId = typeof values.parentId === 'number' ? values.parentId : null;
      // Coerce boolean flags back to 1/0 for API
      const payload = {
        ...values,
        parentId,
        sortOrder: values.sortOrder ?? 999,
        id,
        assignToAllClubs: assignToAllClubs, // Add the new variable to payload
      };
      
      const response = await Common.ApiService.getInstance().request(
        "AddEditEndpoint",
        payload,
        "POST"
      );
      actions.setSubmitting(false);
      setIsSubmitting(false);

      if (response?.status === ResultStatus.Success) {
        Common.showToast("Saved successfully", "Success", "success");
        onClose();
        setOpen(false);
      } else {
        Common.showToast(response?.message || "Failed to save", "Error", "error");
      }
    } catch (error) {
      actions.setSubmitting(false);
      setIsSubmitting(false);
      Common.showToast("Failed to save", "Error", "error");
      console.error(error);
    }
  };
  const treeData = useMemo(() => buildTree(defaultPermissionList), [defaultPermissionList]);
  
  // Smart handler to prevent closing during submission
  const handleOpenChange = (isOpen) => {
    // If trying to close and currently submitting, prevent it
    if (!isOpen && isSubmitting) {
      return;
    }
    setOpen(isOpen);
  };
  
  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent size="xl">
        <DialogHeader>
          <DialogTitle>{id ? "Edit" : "Add"} Routes</DialogTitle>
          <DialogDescription>
            {id ? "Update the route details below" : "Create a new route by filling in the details below"}
          </DialogDescription>
        </DialogHeader>

        <Formik
          initialValues={initialValues}
          enableReinitialize
          validationSchema={validationSchema}
          validateOnChange={false}
          validateOnBlur={false}
          onSubmit={handleSubmit}
        >
          {(formikProps) => (
            <form onSubmit={formikProps.handleSubmit}>
              <div className="space-y-4 mt-4">
                <FormikInput
                  name="name"
                  label="Name"
                  placeholder="Enter name"
                  formikProps={formikProps}
                />
                <FormikInput
                  name="endpoint"
                  label="Endpoint"
                  placeholder="Enter endpoint (eg. /app/your_endpoint)"
                  formikProps={formikProps}
                />
                <FormikInput
                  name="icon"
                  label="Icon"
                  placeholder="Enter icon"
                  formikProps={formikProps}
                />
                <label className="block font-medium text-gray-900">Parent</label>
                <AntDesignTree
                  treeData={treeData}
                  value={(() => {
                    if (formikProps.values.parentId) {
                      return [formikProps.values.parentId];
                    }
                    if (formikProps.values.menutabid !== null && formikProps.values.menutabid !== undefined) {
                      return [`menutab-${formikProps.values.menutabid}`];
                    }
                    return [];
                  })()}
                  onChange={(vals, label, extra) => {
                    const val = vals?.length ? vals[vals.length - 1] : null;
                    formikProps.setFieldValue("parentId", val);

                    let found = null;
                    treeData.forEach(menutab => {
                      menutab.children?.forEach(perm => {
                        if (perm.key === val) found = perm;
                      });
                    });

                    if (found && found.menutabid) {
                      formikProps.setFieldValue("menutabid", found.menutabid);
                    }
                  }}
                  onSelect={(selectedValue, node) => {
                    formikProps.setFieldValue("parentId", node.key);
                    if (node.isMenutab) {
                      if (node.key === "others") {
                        formikProps.setFieldValue("menutabid", null);
                      } else {
                        formikProps.setFieldValue("menutabid", node.menutabid);
                      }
                      // 👉 Auto-assign sortOrder for menutab children (no parentId)
                      const siblings = defaultPermissionList.filter(
                        (item) => item.menutabid === node.menutabid && !item.parentId
                      );
                      const maxSort = Math.max(...siblings.map((s) => s.sortOrder || 0), 0);
                      formikProps.setFieldValue("sortOrder", maxSort + 1);
                    } else {
                      // Child node - set parentId to the actual node key
                      formikProps.setFieldValue("parentId", node.key);
                      if (node.menutabid) {
                        formikProps.setFieldValue("menutabid", node.menutabid);
                      }
                      // 👉 Auto-assign sortOrder for children of this parent
                      const siblings = defaultPermissionList.filter(
                        (item) => item.parentId === node.key
                      );
                      const maxSort = Math.max(...siblings.map((s) => s.sortOrder || 0), 0);
                      formikProps.setFieldValue("sortOrder", maxSort + 1);
                    }
                  }}
                  multiple={true}
                  getPopupContainer={triggerNode => triggerNode.parentNode}
                  placeholder="Select parent"
                />
                {/* Checkboxes and Parent Selector */}
                <div className="flex flex-wrap gap-4 mt-4 justify-center items-center">
                  <AppCheckBox
                    text="Premium"
                    checked={Boolean(formikProps.values.ispremium)}
                    onChange={(checked) =>
                      formikProps.setFieldValue("ispremium", checked)
                    }
                    className="border-default-300"
                  />

                  <AppCheckBox
                    text="Display On"
                    checked={Boolean(formikProps.values.isdisplayon)}
                    onChange={(checked) =>
                      formikProps.setFieldValue("isdisplayon", checked)
                    }
                    className="border-default-300"
                  />

                  <AppCheckBox
                    text="Club Specific"
                    checked={Boolean(formikProps.values.isclubspecific)}
                    onChange={(checked) =>
                      formikProps.setFieldValue("isclubspecific", checked)
                    }
                    className="border-default-300"
                  />

                  <AppCheckBox
                    text="New Feature"
                    checked={Boolean(formikProps.values.isnewfeature)}
                    onChange={(checked) =>
                      formikProps.setFieldValue("isnewfeature", checked)
                    }
                    className="border-default-300"
                  />
                </div>
                <div className="flex flex-wrap gap-4 mt-4 justify-center items-center">
                  
                  </div>
              </div>

              {/* Footer */}
              <DialogFooter className="mt-6">
                <DialogClose asChild>
                  <Button variant="outline">Cancel</Button>
                </DialogClose>
                <Button type="submit" disabled={formikProps.isSubmitting || isSubmitting}>
                  {formikProps.isSubmitting || isSubmitting ? "Saving..." : id ? "Update" : "Add"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

export default AddEditRoutes;
